import { Avatar, Badge, Checkbox, Flex, Tag, Td, Text, Tr } from '@chakra-ui/react';
import { Phone } from 'lucide-react';
import { memo, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { AssignAdminDropdown, type AdminUser } from '../components/AssignAdminDropdown';
import { useCheckPermission } from '../hooks/useCheckPermission';
import { SupportTaskProcessor, SupportTaskStatus } from '@duke-hq/svc-api-db/client';
import { getUserFullName } from '@duke-hq/lib/user';
import { formatTaskStatus, formatTaskType, getTaskStatusDisplay } from './utils/formatters';
import { isTaskAssignable } from '@duke-hq/svc-api/admin/trpc/routers/support/utils/taskUtils';

type TaskItemType = RouterOutputs['support']['task']['list']['items'][number];

function firstNonEmpty(...values: (string | null | undefined)[]): string | null {
  return values.find((v) => v != null && v !== '') ?? null;
}

function SupportTaskRowComponent({
  task,
  users,
  onTaskClick,
  isSelected = false,
  onSelectChange,
}: {
  task: TaskItemType;
  users: AdminUser[];
  onTaskClick: (taskId: string) => void;
  isSelected?: boolean;
  onSelectChange?: (taskId: string, checked: boolean) => void;
}) {
  const utils = trpcAdmin.useUtils();
  const hasManagePermission = useCheckPermission('canManageSupportTasks');

  const handleRowClick = useCallback(() => onTaskClick(task.id), [onTaskClick, task.id]);
  const handleSelectChange = useCallback(
    (checked: boolean) => onSelectChange?.(task.id, checked),
    [onSelectChange, task.id],
  );

  const assignTaskMutation = trpcAdmin.support.task.assign.useMutation({
    onSuccess: () => {
      void utils.support.task.list.invalidate(undefined, { refetchType: 'active' });
    },
  });

  const handleAssignmentChange = (assignedToDrn: string | null) => {
    assignTaskMutation.mutate({
      taskId: task.id,
      assignedToDrn,
    });
  };

  const taskWithRelations = task as typeof task & {
    assignedTo?: {
      id: string;
      drn: string | null;
      first_name: string;
      last_name: string | null;
      email: string | null;
      picture_url: string | null;
    } | null;
  };

  // Format display names from enriched data using utility functions
  const studentName =
    task.student != null
      ? (() => {
          const name = getUserFullName(task.student);
          return name != null && name !== '' ? name : t`Unknown Student`;
        })()
      : t`Unknown Student`;
  const studentEmail = firstNonEmpty(task.student?.email) ?? t`N/A`;
  const studentPhone = firstNonEmpty(task.student?.phone_number) ?? t`N/A`;

  // Format contract type and provider from enriched data
  const contractType =
    firstNonEmpty(task.enrolment?.enrolment_contract_type, task.enrolment?.contract_category) ??
    t`N/A`;
  const providerName = firstNonEmpty(task.provider?.name) ?? t`Unknown Provider`;

  // Only allow assignment for HUMAN processor tasks that are not COMPLETED
  const isAutomated = task.processor === SupportTaskProcessor.AUTOMATED;
  const isCompleted = task.status === SupportTaskStatus.COMPLETED;
  const canAssign = isTaskAssignable(task);
  const cannotAssignReason = isAutomated
    ? t`Automated tasks cannot be assigned`
    : isCompleted
      ? t`Completed tasks cannot be reassigned`
      : undefined;

  // Get status display properties
  const statusDisplay = getTaskStatusDisplay(task.status);

  return (
    <Tr onClick={handleRowClick} cursor="pointer" _hover={{ bg: 'bg.subtle' }}>
      {/* Checkbox Column */}
      <Td onClick={(e) => e.stopPropagation()}>
        {onSelectChange ? (
          <Checkbox
            isChecked={isSelected}
            onChange={(e) => handleSelectChange(e.target.checked)}
            isDisabled={!canAssign}
            title={cannotAssignReason}
          />
        ) : null}
      </Td>

      {/* Assignee Column */}
      <Td onClick={(e) => e.stopPropagation()}>
        {isAutomated ? (
          <Text fontSize="sm" color="fg.muted">
            <Trans>Automated</Trans>
          </Text>
        ) : !canAssign && taskWithRelations.assignedTo ? (
          <Flex align="center" gap={2}>
            <Avatar
              size="xs"
              name={getUserFullName(taskWithRelations.assignedTo) ?? undefined}
              src={taskWithRelations.assignedTo.picture_url ?? undefined}
            />
            <Text fontSize="sm">{getUserFullName(taskWithRelations.assignedTo)}</Text>
          </Flex>
        ) : !canAssign ? (
          <Text fontSize="sm" color="fg.muted">
            <Trans>—</Trans>
          </Text>
        ) : (
          <AssignAdminDropdown
            currentAssignee={taskWithRelations.assignedTo ?? undefined}
            onAssign={handleAssignmentChange}
            selfAssignOnly={!hasManagePermission}
            enabled={canAssign}
            users={users}
          />
        )}
      </Td>

      {/* Participant Column */}
      <Td>
        <Flex align="center">
          <Avatar size="sm" name={studentName} />
          <Flex direction="column" ml={3}>
            <Text fontWeight="medium" fontSize="sm">
              {studentName}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {studentEmail}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {studentPhone}
            </Text>
          </Flex>
        </Flex>
      </Td>

      {/* Status Column */}
      <Td>
        <Badge
          fontSize="xs"
          fontWeight="bold"
          borderRadius="full"
          colorScheme={statusDisplay.colorScheme}
          variant={statusDisplay.variant}
          textTransform="none"
        >
          {formatTaskStatus(task.status)}
        </Badge>
      </Td>

      {/* Type Column */}
      <Td>
        <Text fontSize="sm">{formatTaskType(task.kind, task.title)}</Text>
      </Td>

      {/* Contract Type Column */}
      <Td>
        <Text fontSize="sm" color="fg.muted">
          {contractType}
        </Text>
      </Td>

      {/* Provider Column */}
      <Td>
        <Text fontSize="sm" color="fg.muted">
          {providerName}
        </Text>
      </Td>

      {/* Action Column */}
      <Td>
        {!isAutomated ? (
          <Tag
            fontSize="xs"
            fontWeight="bold"
            borderRadius="full"
            colorScheme="purple"
            variant="solid"
            cursor="pointer"
            as="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick();
            }}
          >
            <Flex align="center" gap={1}>
              <Phone size={12} />
              <Text>
                <Trans>Call</Trans>
              </Text>
            </Flex>
          </Tag>
        ) : null}
      </Td>
    </Tr>
  );
}

export const SupportTaskRow = memo(SupportTaskRowComponent);
